import config from "../../../config/backend";
import type {
  TeacherRepository,
  UpdateTeacherBody,
  UpdateTeacherProfessionalInfoBody,
  UpdateTeacherTeachingBody,
  UpdateTeacherPublicProfileBody,
  TeacherPublicProfilePreviewPatch,
  TeacherPublicProfilePreviewVM,
  TeacherStudentDetails,
  TeacherStudentListElement,
  TeacherStudioListItem,
  CreateTeacherStudioBody,
  UpdateTeacherStudioBody,
  LessonCreationStudiosVM,
  CreateLessonBody,
  TeacherLessonListVM,
  TeacherVM,
  CollaborationRequestBody,
  CollaborationRequestVM,
  RequestTeacherPayoutBody,
  TeacherPayoutEligibilityVM,
  TeacherPayoutVM,
  UnpaidLessonVM,
} from "@shared-kernel/application/ports/teacher/teacher-repository";
import type { TokenStorageGateway } from "@shared-kernel/application/ports/token-storage-gateway";
import { HttpGateway } from "@shared-kernel/secondary/http-gateway";

export class BackendTeacherRepository extends HttpGateway implements TeacherRepository {
  private _route: string = "v1/teacher";
  private _lessonRoute: string = "v1/teacher/lessons";
  private _collaborationTeacherRoute: string = "v1/teacher/collaboration-requests";
  private _collaborationAdminRoute: string = "v1/admin/collaboration-requests";

  constructor(tokenStorageGateway: TokenStorageGateway) {
    super(config.url, tokenStorageGateway);
  }

  async profile(): Promise<TeacherVM> {
    const { data: teacher } = await this._instance.get(`${this._route}/profile`);
    return teacher;
  }

  async update(body: UpdateTeacherBody): Promise<string> {
    const { data: teacherId } = await this._instance.put(`${this._route}/profile`, body);
    return teacherId;
  }

  async updateProfessionalInfo(body: UpdateTeacherProfessionalInfoBody): Promise<void> {
    await this._instance.put(`${this._route}/profile/professional-info`, body);
  }

  async updateTeaching(body: UpdateTeacherTeachingBody): Promise<void> {
    await this._instance.put(`${this._route}/profile/teaching`, body);
  }

  async updatePublicProfile(body: UpdateTeacherPublicProfileBody): Promise<void> {
    await this._instance.put(`${this._route}/profile/public-profile`, body);
  }

  async previewPublicProfile(patch: TeacherPublicProfilePreviewPatch): Promise<TeacherPublicProfilePreviewVM> {
    const { data } = await this._instance.post(`${this._route}/profile/public-profile/preview`, patch);
    return data;
  }

  async removeFromStudentFavorite(studentId: string): Promise<void> {
    await this._instance.delete(`${this._route}/favorite-students/${studentId}`);
  }

  async updateFavoriteStudio(favoriteStudio: { id: string }): Promise<void> {
    await this._instance.put(`${this._route}/favorite-studio`, { ...favoriteStudio });
  }

  async all(): Promise<TeacherStudentListElement[]> {
    const { data: students } = await this._instance.get(`${this._route}/students`);
    return students;
  }

  async getStudentByEmail(email: string): Promise<TeacherStudentDetails> {
    const { data: student } = await this._instance.get(`${this._route}/students:by-email`, {
      params: {
        email,
      },
    });
    return student;
  }

  async getStudioById(id: string): Promise<TeacherStudioListItem> {
    const { data } = await this._instance.get<TeacherStudioListItem>(`${this._route}/studios/${id}`);
    return data;
  }

  async createStudio(body: CreateTeacherStudioBody): Promise<string> {
    const { data } = await this._instance.post(`${this._route}/studios`, body);
    return data;
  }

  async updateStudio(body: UpdateTeacherStudioBody): Promise<void> {
    await this._instance.put(`${this._route}/studios/${body.id}`, body);
  }

  async getLessonCreationStudios(): Promise<LessonCreationStudiosVM> {
    const { data } = await this._instance.get<LessonCreationStudiosVM>(`${this._route}/lesson-creation-studios`);
    return data;
  }

  // Lessons
  async allLessons(): Promise<TeacherLessonListVM[]> {
    const { data: lessons } = await this._instance.get(this._lessonRoute);
    return lessons;
  }

  async createLesson(lesson: CreateLessonBody): Promise<string> {
    const { data: lessonId } = await this._instance.post(this._lessonRoute, lesson);
    return lessonId;
  }

  async cancelLesson(lessonId: string): Promise<void> {
    await this._instance.delete(`${this._lessonRoute}/${lessonId}`);
  }

  // Collaboration Requests
  async requestCollaboration(collaboration: CollaborationRequestBody): Promise<void> {
    await this._instance.post(this._collaborationTeacherRoute, collaboration);
  }

  async listCollaborationRequests(): Promise<CollaborationRequestVM[]> {
    const { data } = await this._instance.get(this._collaborationAdminRoute);
    return data;
  }

  async approveCollaborationRequest({ id, status }: { id: string; status: "approved" | "rejected" }): Promise<void> {
    await this._instance.post(`${this._collaborationAdminRoute}/${id}:approve`, { status });
  }

  // Payouts
  async requestTeacherPayout(command: RequestTeacherPayoutBody): Promise<void> {
    await this._instance.post(`${this._route}/teacher-payouts`, command);
  }

  async getPayoutEligibility(): Promise<TeacherPayoutEligibilityVM> {
    const response = await this._instance.get(`${this._route}/payout-request-eligibility`);
    return response.data;
  }

  async getTeacherPayouts(): Promise<TeacherPayoutVM[]> {
    const response = await this._instance.get(`${this._route}/payouts`);
    return response.data;
  }

  async getUnpaidLessons(): Promise<UnpaidLessonVM[]> {
    const response = await this._instance.get(`${this._route}/unpaid-lessons`);
    return response.data;
  }
}
